import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';

import intlShape from '@/shared/utils/intlShape';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import messages from '../dashboardMessages';
import ComplianceOverviewChart from './ComplianceOverviewChart';
import CompliancePerTypeChart from './CompliancePerTypeChart';

const ComplianceDashboard = ({ intl, companyId }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [rawData, setRawData] = useState(null);

  const toggleMode = () => {
    setShowDetails(!showDetails);
  };

  useEffect(() => {
    setRawData(null);
    const cancelToken = Axios.CancelToken.source();

    api.base
      .get(`${config.appUrls.api}/api/compliance/statistics/`, {
        params: {
          companyId,
        },
        cancelToken: cancelToken.token,
      })
      .then(({ data }) => {
        setRawData(data || {});
      }, handleHttpError(intl));
    return () => {
      if (cancelToken) cancelToken.cancel('Operation canceled due to new request.');
    };
  }, [companyId, intl]);

  const loading = !rawData;
  const hasData = !!(rawData && rawData.complianceForTypes && rawData.complianceForTypes.length);

  return (
    <div className="dashboard-chart">
      <div className="title-block">
        <FormattedMessage
          {...(showDetails ? messages.CompliancePerCriterion : messages.ComplianceOverview)}
        />
        {!!rawData && (
          <a
            className="right"
            title={intl.formatMessage(
              showDetails
                ? messages.SwitchToCompliancyOverviewForAllCustomers
                : messages.SwitchToCompliancyPerType,
            )}
            onClick={() => toggleMode()}
          >
            <i className={classnames('fa', showDetails ? 'fa-pie-chart' : 'fa-bar-chart')} />
          </a>
        )}
      </div>
      <div
        className={classnames('content-block zero-customer-debt', { nodata: !loading && !hasData })}
      >
        {loading && <LoadingAnimation />}
        {!loading && hasData && (
          <div className="chart-container">
            {showDetails ? (
              <CompliancePerTypeChart data={rawData} />
            ) : (
              <ComplianceOverviewChart data={rawData} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ComplianceDashboard.propTypes = {
  intl: intlShape.isRequired,
  companyId: PropTypes.string.isRequired,
};

export default injectIntl(ComplianceDashboard);
